/**
 * Unencrypted database connections need the client's explicit acknowledgment.
 *
 * With TLS mode `disable` the login and every query result travel in
 * plaintext between maic and the client's database. connector-service refuses
 * such a save unless `config.insecure_transport_ack` is true, and records who
 * accepted it and when (backend/connector_service/insecure_transport.py). The
 * API returns `transport_unencrypted` and `insecure_transport_acknowledged` on
 * every database connector so the UI can say so.
 */
import { DbSslMode, normalizeSslMode } from './dbTls';

export const ACK_REQUEST_KEY = 'insecure_transport_ack';

export interface InsecureTransportAckRecord {
  by?: string;
  user_id?: string | null;
  at?: string;
  mode?: string;
  active?: boolean;
  deactivated_at?: string;
  deactivated_by?: string;
}

// SQL Server's driver only turns encryption off for these spellings.
const MSSQL_PLAINTEXT = new Set(['false', 'disable', 'no', '0']);

/** The `ssl` / `sslmode` a connection URL carries, raw ('' when none). */
export function urlSslValue(baseUrl: string): string {
  const q = (baseUrl || '').split('?')[1];
  if (!q) return '';
  const params = new URLSearchParams(q);
  return params.get('ssl') ?? params.get('sslmode') ?? '';
}

function isSqlServer(connectorType: string, baseUrl: string): boolean {
  return connectorType === 'SQLSERVER' || /^\s*(sqlserver|mssql):/i.test(baseUrl || '');
}

/**
 * Whether a database connector with this form state would run in plaintext.
 * The form's mode wins over the URL, as the backend merges them (an explicit
 * mode in config beats `?sslmode=` in the URL).
 */
export function wouldBeUnencrypted(connectorType: string, mode: DbSslMode, baseUrl: string): boolean {
  if (isSqlServer(connectorType, baseUrl)) {
    const raw = urlSslValue(baseUrl).trim().toLowerCase();
    return MSSQL_PLAINTEXT.has(raw);
  }
  if (mode) return mode === 'disable';
  return normalizeSslMode(urlSslValue(baseUrl)) === 'disable';
}

export type AckRequirement = 'none' | 'required' | 'optional';

/**
 * What the form asks for. `required`: this save enters plaintext, so it is
 * refused without the checkbox. `optional`: the connector already runs in
 * plaintext with no acknowledgment on file (set before the rule existed);
 * ticking the box records it, leaving it unticked still saves.
 */
export function ackRequirement(opts: {
  nowUnencrypted: boolean;
  wasUnencrypted: boolean;
  record?: InsecureTransportAckRecord | null;
}): AckRequirement {
  if (!opts.nowUnencrypted) return 'none';
  if (!opts.wasUnencrypted) return 'required';
  const active = Boolean(opts.record) && opts.record?.active !== false;
  return active ? 'none' : 'optional';
}

/** "2026-10-26 14:05 UTC" from an ISO timestamp. */
export function formatAckDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}
